import { ScrollText } from 'lucide-react';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { Fragment, useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Download, ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';
import { formatINR } from '@/shared/utils/currency';

interface Gstr2bCategoryRow {
  id: string;
  category: string;
  docsCount: number | string;
  taxableValue: number;
  totalGst: number;
  isExpandable?: boolean;
  subRows?: Array<{
    id: string;
    category: string;
    docsCount: number | string;
    taxableValue: number;
    totalGst: number;
  }>;
}

const money = (v: number) => Math.round((Number(v) || 0) * 100) / 100;
const gstOf = (d: any) => money(d?.igst) + money(d?.cgst) + money(d?.sgst);

/**
 * This screen used to render a hardcoded table — 16 documents, ₹10,00,000,
 * ₹1,80,000 GST, and two rows whose "Docs Count" cell was the STRING
 * '₹10,00,000' — with no API call at all. An accountant reading it would plan
 * input-tax credit against numbers that do not exist.
 *
 * It now reports the company's own posted purchase bills for the period, via
 * GET /api/gstr/gstr2 (src/api-gstr/index.js:271, requires reports.read).
 * IMPORTANT DISTINCTION, said plainly on screen: the portal's GSTR-2B is a
 * statement issued by the GSTN against what your SUPPLIERS filed. Nothing in
 * this app can produce that without the GST portal (an external dependency), so
 * this is inward supply from our records — the input to a reconciliation, never
 * the reconciled result.
 */
export function Gstr2bReportComponent() {
  const navigate = useNavigate();
  const [mainTab, setMainTab] = useState<'summary' | 'vendor' | 'detailed'>('summary');
  const [itcTab, setItcTab] = useState<'available' | 'unavailable'>('available');
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({ b2b: true });
  const [gstin, setGstin] = useState<string>('');

  const report = useQuery({
    queryKey: ['gstr2', gstin],
    queryFn: async () => (await api.get('/api/gstr/gstr2', { params: { ...(gstin ? { gstin } : {}) } })).data,
  });

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const b2b = Array.isArray(report.data?.b2b) ? report.data.b2b : [];
  const b2bur = Array.isArray(report.data?.b2bur) ? report.data.b2bur : [];
  const excluded = Array.isArray(report.data?.excluded) ? report.data.excluded : [];

  const rows: Gstr2bCategoryRow[] = report.data ? [
    {
      id: 'b2b',
      category: 'Inward supplies from registered vendors (B2B)',
      docsCount: b2b.length,
      taxableValue: money(b2b.reduce((s: number, d: any) => s + money(d.taxableValue), 0)),
      totalGst: money(b2b.reduce((s: number, d: any) => s + gstOf(d), 0)),
      isExpandable: true,
      subRows: b2b.slice(0, 40).map((d: any, i: number) => ({
        id: `b2b-${i}`,
        category: `${d.supplierName || d.supplierGstin || 'vendor'} — ${d.billNumber || d.invoiceNumber || ''}`.trim(),
        docsCount: 1,
        taxableValue: money(d.taxableValue),
        totalGst: gstOf(d),
      })),
    },
    {
      id: 'b2bur',
      category: 'Unregistered / imports (B2BUR)',
      docsCount: b2bur.length,
      taxableValue: money(b2bur.reduce((s: number, d: any) => s + money(d.taxableValue), 0)),
      totalGst: money(b2bur.reduce((s: number, d: any) => s + gstOf(d), 0)),
      isExpandable: b2bur.length > 0,
    },
    {
      id: 'excluded',
      category: 'Bills held back from filing (not eligible / incomplete)',
      docsCount: excluded.length,
      taxableValue: 0,
      totalGst: 0,
      isExpandable: false,
    },
  ] : [];

  const totals = report.data?.totals || { taxableValue: 0, igst: 0, cgst: 0, sgst: 0 };

  const downloadCsv = () => {
    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = [
      ['Category', 'Documents', 'Taxable value', 'Total GST'].map(esc).join(','),
      ...rows.map((r) => [r.category, r.docsCount, r.taxableValue, r.totalGst].map(esc).join(',')),
      ['TOTAL', report.data?.b2b?.length ?? 0, totals.taxableValue, money(totals.igst + totals.cgst + totals.sgst)].map(esc).join(','),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([lines], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `inward-supplies-${report.data?.period || 'period'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Indian grouping through the tested formatter; `money()` above already pins
  // every value to 2dp, so no rounding is introduced by displaying it.
  const formatMoney = (val: number) => formatINR(val);

  return (
    <div className="space-y-6">
      {/* Breadcrumb & Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <PageHeader icon={ScrollText} eyebrow="GST & Compliance" title="GSTR-2B Purchase Report" description="Inward supplies from your own posted purchase bills, for the selected registration and period." />

        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="h-4 w-4" /> Back</Button>
          {(report.data?.availableRegistrations || []).length > 1 && (
            <select
              aria-label="Registration"
              className="h-9 rounded-lg border border-input bg-background px-3 text-body-sm text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={gstin}
              onChange={(e) => setGstin(e.target.value)}
            >
              <option value="">All registrations</option>
              {(report.data.availableRegistrations || []).map((r: any) => (
                <option key={r.gstin} value={r.gstin}>{r.gstin} — {r.legalName}</option>
              ))}
            </select>
          )}
          {/* These two buttons used to be no-ops that only closed their own
              menu. Export now produces the real rows, and "Reconcile" is gone
              rather than faked: reconciling against the portal's 2B needs the
              GSTN, which this app does not connect to. */}
          <Button
            onClick={downloadCsv}
            variant="outline"
            className="flex items-center gap-2"
            disabled={!report.data}
          >
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </div>
      </div>

      <p className="rounded-card border border-info/20 bg-info/10 px-4 py-3 text-body-sm text-info-text">
        The GST portal’s GSTR-2B statement is issued by the GSTN against what your suppliers filed,
        and this system does not connect to the portal. These figures come from the purchase bills
        recorded here — they are the input to a reconciliation, not a reconciled result.
      </p>

      {report.isLoading && (
        <div className="space-y-2 rounded-card border border-border bg-card p-4 shadow-xs" aria-busy="true" aria-live="polite">
          <Skeleton className="h-8 w-64" />
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      )}
      {report.isError && (
        <div role="alert" className={`rounded-card border p-4 text-sm shadow-xs ${getApiErrorStatus(report.error) === 403 ? 'border-warning/25 bg-warning/10 text-warning-text' : 'border-danger/20 bg-danger/10 text-danger-text'}`}>
          <p className="font-semibold">{getApiErrorStatus(report.error) === 403 ? 'Permission denied' : 'The inward-supply report could not be loaded.'}</p>
          <p className="mt-1 text-body-sm">
            {getApiErrorMessage(report.error, 'The GSTR-2 report service did not respond. Try again, then check that a GST registration is configured.')}
          </p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => report.refetch()}>Try again</Button>
        </div>
      )}
      {report.data && !b2b.length && !b2bur.length && (
        <div className="rounded-card border border-border bg-card p-4 shadow-xs">
          <EmptyState
            title="No posted purchase bills in this period"
            description="Draft bills are excluded by design, so this is a real nil — not a missing file. Post the purchase bills for this period, then refresh."
            action={<Button size="sm" variant="outline" onClick={() => report.refetch()}>Refresh</Button>}
          />
        </div>
      )}

      {/* Period the report was built for, stated before the figures. */}
      {report.data && (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Period</span>
          <span className="font-mono tabular-nums text-data-lg font-semibold text-on-surface">{report.data?.period || 'not reported by the server'}</span>
        </div>
      )}

      {/* Main Mode Tabs */}
      <div className="flex items-center gap-6 border-b border-border text-body-md font-semibold">
        {([['summary', 'Summary'], ['vendor', 'Vendor view'], ['detailed', 'Detailed report']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setMainTab(key)}
            aria-current={mainTab === key ? 'page' : undefined}
            className={`border-b-2 pb-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              mainTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ITC Availability Tabs */}
      <div className="flex items-center gap-6 border-b border-border text-body-sm font-semibold">
        {([['available', 'ITC available'], ['unavailable', 'ITC unavailable']] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setItcTab(key)}
            aria-current={itcTab === key ? 'page' : undefined}
            className={`border-b-2 pb-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              itcTab === key
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Category Table */}
      <div className="overflow-x-auto rounded-card border border-border bg-card shadow-xs">
        <table className="w-full text-body-md text-left border-collapse">
          <thead className="border-b border-border bg-muted text-label-caps uppercase text-on-surface-variant">
            <tr>
              <th className="px-5 py-2.5">Category</th>
              <th className="px-5 py-2.5 text-right">No. of documents</th>
              <th className="px-5 py-2.5 text-right">Taxable value</th>
              <th className="px-5 py-2.5 text-right">Total GST</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((row) => {
              const isExpanded = expandedRows[row.id];
              return (
                <Fragment key={row.id}>
                  <tr className="hover:bg-muted/50">
                    <td className="px-5 py-2.5 font-medium text-on-surface">
                      <div className="flex items-center gap-2">
                        {row.isExpandable && (
                          <button
                            onClick={() => toggleRow(row.id)}
                            aria-expanded={!!isExpanded}
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${row.category}`}
                            className="rounded p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-on-surface-variant" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-on-surface-variant" />
                            )}
                          </button>
                        )}
                        <span>{row.category}</span>
                      </div>
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono tabular-nums text-on-surface">{row.docsCount}</td>
                    <td className="px-5 py-2.5 text-right font-mono tabular-nums text-on-surface">
                      {typeof row.taxableValue === 'number' ? formatMoney(row.taxableValue) : row.taxableValue}
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono tabular-nums text-on-surface">
                      {typeof row.totalGst === 'number' ? formatMoney(row.totalGst) : row.totalGst}
                    </td>
                  </tr>

                  {/* Sub-rows */}
                  {isExpanded &&
                    row.subRows?.map((sub) => (
                      <tr
                        key={sub.id}
                        className="bg-muted/30 hover:bg-muted/50"
                      >
                        <td className="py-2.5 pl-12 pr-5 text-body-sm font-medium text-on-surface-variant">
                          <div className="flex items-center gap-2">
                            <ChevronRight className="h-3.5 w-3.5 text-on-surface-subtle" />
                            <span>{sub.category}</span>
                          </div>
                        </td>
                        <td className="py-2.5 pr-5 text-right font-mono tabular-nums text-body-sm text-on-surface-variant">{sub.docsCount}</td>
                        <td className="py-2.5 pr-5 text-right font-mono tabular-nums text-body-sm text-on-surface-variant">{formatMoney(sub.taxableValue)}</td>
                        <td className="py-2.5 pr-5 text-right font-mono tabular-nums text-body-sm text-on-surface-variant">{formatMoney(sub.totalGst)}</td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
          {/* Totals come from the report's own `totals`, not from the rows on show. */}
          {report.data && (
            <tfoot>
              <tr className="border-t border-border bg-muted/50">
                <td className="px-5 py-3 text-right">
                  <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Total</span>
                </td>
                <td className="px-5 py-3 text-right font-mono tabular-nums text-on-surface">{b2b.length}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums font-semibold text-on-surface">{formatMoney(totals.taxableValue)}</td>
                <td className="px-5 py-3 text-right font-mono tabular-nums font-semibold text-on-surface">{formatMoney(money(totals.igst + totals.cgst + totals.sgst))}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Reconciliation note — says what to do with these figures, adds none. */}
      <p className="rounded-card border border-border bg-muted/40 px-4 py-3 text-body-sm text-on-surface-variant">
        Reconcile these against the GSTR-2B statement downloaded from the GST portal before claiming credit. A bill
        missing here is a posting problem in this system; a bill missing on the portal is a supplier problem. They are
        fixed differently.
      </p>
    </div>
  );
}
